import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const getGuestIdFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
};

const decryptUserData = async (encryptedData) => {
  try {
    if (encryptedData.email && encryptedData.email.length < 50) {
      return encryptedData;
    }

    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        encrypted_fields: {
          email: encryptedData.email,
          phone_number: encryptedData.phone_number,
          country_code: encryptedData.country_code,
        },
      }),
    });

    if (response.status === 401) {
      return encryptedData;
    }

    if (!response.ok) {
      throw new Error(`Decryption failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData;
  }
};

const getGuestData = async () => {
  const accessToken = Cookies.get('access_token');
  if (!accessToken) return null;

  try {
    const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
    if (!guestId) return null;

    const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!profileResponse.ok) {
      throw new Error(`Profile API Error: ${profileResponse.statusText}`);
    }

    const profileData = await profileResponse.json();
    const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

    if (isDecrypted) {
      return {
        first_name: profileData.firstname || '',
        last_name: profileData.lastname || '',
        email: profileData.emailid || '',
        phone_number: profileData.phonenumber || '',
        country_code: '+91',
        client_type: 'Leisure',
        points: profileData.points || '100',
        last_stay: profileData.last_stay || 'January 2026',
        member_status: profileData.member_status || 'Silver Member',
      };
    }

    const decryptedFields = await decryptUserData({
      email: profileData.emailid,
      phone_number: profileData.phonenumber,
      country_code: profileData.countrycode,
    });

    return {
      first_name: profileData.firstname || '',
      last_name: profileData.lastname || '',
      email: decryptedFields.email || profileData.emailid || '',
      phone_number: decryptedFields.phone_number || profileData.phonenumber || '',
      country_code: '+91',
      client_type: 'Leisure',
      points: profileData.points || '100',
      last_stay: profileData.last_stay || 'August 2025',
      member_status: profileData.member_status || 'Gold Member',
    };
  } catch (error) {
    console.error('Error fetching guest data:', error);
    return null;
  }
};

const WalkReservation = () => {
  const [guestName, setGuestName] = useState(null);
  const [memberStatus, setMemberStatus] = useState(null);
  const [points, setPoints] = useState(null);
  const [lastStay, setLastStay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGuestData = async () => {
      setIsLoading(true);
      const guestData = await getGuestData();
      if (guestData) {
        const fullName = `${guestData.first_name} ${guestData.last_name}`;
        setGuestName(fullName);
        setMemberStatus(guestData.member_status);
        setPoints(guestData.points);
        setLastStay(guestData.last_stay);
      }
      setIsLoading(false);
    };

    fetchGuestData();
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white min-h-screen flex flex-col items-center p-4 md:p-6">
        {/* Back Arrow Section */}
        <div className="mt-13">
          <Link
            to="/"
            className="text-black hover:text-gray-700 transition font-semibold text-2xl inline-block absolute top-24 left-8 "
          >
            <IoMdArrowRoundBack size={32} />
          </Link>
        </div>

        {/* Content Section */}
        <div className="w-full max-w-6xl mt-28">
          <div className="text-left mb-8">
            <div className="h-12 bg-gray-200 rounded w-3/4 mb-4 animate-pulse"></div>
            <div className="flex gap-4">
              <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-36 animate-pulse"></div>
            </div>
          </div>

          <div className="text-center mt-10">
            <div className="h-12 bg-gray-200 rounded w-3/4 mx-auto mb-8 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-100 p-6 rounded-xl border border-gray-100">
                <div className="text-center">
                  <div className="bg-gray-300 w-16 h-16 rounded-full mx-auto mb-4 animate-pulse"></div>
                  <div className="h-8 bg-gray-300 rounded w-3/4 mx-auto mb-4 animate-pulse"></div>
                  <div className="h-4 bg-gray-300 rounded w-full mb-6 animate-pulse"></div>
                  <div className="h-10 bg-gray-300 rounded-lg w-32 mx-auto animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex flex-col items-center p-4 md:p-8">
      {/* Back Arrow Section */}
      <div className="w-full  mt-16">
        <Link
          to="/"
          className="text-black hover:text-gray-700 transition font-semibold text-2xl inline-block"
        >
          <IoMdArrowRoundBack size={32} />
        </Link>
      </div>

      {/* Content Section */}
      <div className=" max-w-6xl">
        {/* Welcome Section */}
        <div className="text-left mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Welcome,{' '}
            {guestName
              ? guestName
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
              : 'Guest'}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
            {memberStatus && <span className="font-medium">{memberStatus}</span>}
            {points && <span>Points: {points}</span>}
            {/* {lastStay && <span>Last Stay: {lastStay}</span>} */}
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 mt-10">
            What would you like to do today?
          </h2>
        </div>

        {/* Cards Container */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {/* Reservation Card */}
          <div className="bg-gray-100 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div className="text-center">
              <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-poppins sm:text-2xl font-bold text-gray-800 mb-3">
                Reservation
              </h2>
              <p className="text-gray-600 font-poppins mb-6">
                New here? Reserve your stay with us for a luxurious experience
              </p>
              <Link to="/profile_guest_options">
                <button className="bg-black text-white px-8 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                  Reserve Now
                </button>
              </Link>
            </div>
          </div>

          {/* Check-In Card */}
          <div className="bg-gray-100 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div className="text-center">
              <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-poppins sm:text-2xl font-bold text-gray-800 mb-3">
                Check-In
              </h2>
              <p className="text-gray-600 font-poppins mb-6">
                Already Reserved? Check in quickly and start your stay
              </p>
              <Link to="/reservation/options">
                <button className="bg-black text-white px-8 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                  Check-In
                </button>
              </Link>
            </div>
          </div>

          {/* Check-Out Card */}
          <div className="bg-gray-100 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div className="text-center">
              <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-poppins sm:text-2xl font-bold text-gray-800 mb-3">
                Check-Out
              </h2>
              <p className="text-gray-600 font-poppins mb-6">
                Leaving? Check out easily and we hope to see you again
              </p>
              <Link to="/check-out-options">
                <button className="bg-black text-white px-8 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                  Check-Out
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalkReservation;
